import type { IconName } from '../models/types';

/**
 * Çizgi ikon seti.
 *
 * İkonlar hazır bir kütüphaneden değil, doğrudan SVG olarak yazıldı —
 * tasarım prototipindeki çizimlerin aynısı. Böylece ne ek bir paket
 * indiriliyor, ne de ikonlar için ayrı bir dosya isteği yapılıyor.
 *
 * Hepsi 24×24'lük bir kareye çizilmiştir ve yalnızca ÇİZGİDEN oluşur
 * (`fill: none`), bu yüzden rengi ve kalınlığı dışarıdan verilir.
 */

/** Her ikonun 24×24 kutu içindeki çizim parçaları. */
const ICON_SHAPES: Record<IconName, string> = {
  water: '<path d="M12 3c4 5 6 8 6 11a6 6 0 01-12 0c0-3 2-6 6-11z"/>',
  moon: '<path d="M20 14a8 8 0 11-8-10 7 7 0 008 10z"/>',
  walk: '<ellipse cx="9" cy="7" rx="2.2" ry="3"/><ellipse cx="15" cy="15" rx="2.2" ry="3"/>',
  smoke: '<circle cx="12" cy="12" r="9"/><line x1="5" y1="19" x2="19" y2="5"/>',
  book: '<path d="M4 5c3-1 6-1 8 0v14c-2-1-5-1-8 0V5z"/><path d="M20 5c-3-1-6-1-8 0v14c2-1 5-1 8 0V5z"/>',
  apple: '<path d="M12 7c-3 0-5 2-5 6a5 5 0 0010 0c0-4-2-6-5-6z"/><line x1="12" y1="7" x2="12" y2="4"/>',
  broom: '<line x1="17" y1="2" x2="9" y2="14"/><path d="M9 14l-4 6 10-4-1-6z"/>',
  dumbbell:
    '<line x1="4" y1="12" x2="20" y2="12"/><rect x="2" y="9" width="4" height="6" rx="1"/><rect x="18" y="9" width="4" height="6" rx="1"/>',
  laptop:
    '<rect x="4" y="5" width="16" height="10" rx="1"/><line x1="2" y1="19" x2="22" y2="19"/>',
  social:
    '<path d="M4 6h12a2 2 0 012 2v6a2 2 0 01-2 2H9l-5 3V6z"/><line x1="3" y1="3" x2="21" y2="21"/>',
  toothbrush:
    '<rect x="3" y="10" width="10" height="4" rx="2"/><line x1="13" y1="12" x2="21" y2="12"/><line x1="5" y1="8" x2="5" y2="6"/><line x1="8" y1="8" x2="8" y2="6"/><line x1="11" y1="8" x2="11" y2="6"/>',
  cube: '<rect x="6" y="6" width="12" height="12" rx="2"/>',
  home: '<path d="M3 11l9-8 9 8"/><path d="M5 10v9h14v-9"/>',
  trophy:
    '<path d="M8 4h8v4a4 4 0 01-8 0V4z"/><path d="M6 6H4a2 2 0 002 4"/><path d="M18 6h2a2 2 0 01-2 4"/><line x1="10" y1="14" x2="10" y2="18"/><line x1="14" y1="14" x2="14" y2="18"/><line x1="8" y1="18" x2="16" y2="18"/>',
  checklist: '<rect x="4" y="4" width="16" height="16" rx="4"/><path d="M8 12l3 3 5-6"/>',
  user: '<circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>',
  flame:
    '<path d="M12 2c2 4-3 5-3 9a5 5 0 0010 0c0-2-1-3-2-4 0 2-1 3-2 3-2 0-2-2-1-4-1 0-2-2-2-4z"/>',
  gift: '<rect x="4" y="9" width="16" height="11" rx="2"/><line x1="4" y1="13" x2="20" y2="13"/><line x1="12" y1="9" x2="12" y2="20"/><path d="M12 9c-1-4-6-4-6-1s3 1 6 1z"/><path d="M12 9c1-4 6-4 6-1s-3 1-6 1z"/>',
  // Kalem: "düzenle" eylemlerinde kullanılır. Gövde + uç çizgisi.
  pencil: '<path d="M4 20l4.5-1L20 7.5 16.5 4 5 15.5 4 20z"/><line x1="15" y1="5.5" x2="18.5" y2="9"/>',
};

export interface IconOptions {
  /** Kenar uzunluğu (piksel). İkonlar kare çizilir. */
  size: number;
  /** Çizgi rengi. Herhangi bir CSS rengi ya da `currentColor` olabilir. */
  color?: string;
  /** Çizgi kalınlığı. Küçük boyutlarda biraz artırmak okunurluğu artırır. */
  strokeWidth?: number;
}

/**
 * Bir ikonu SVG metni olarak üretir.
 *
 * Rengin varsayılanı `currentColor`: yani ikon, içinde bulunduğu
 * elemanın `color` değerini devralır. Bu sayede ikon rengini CSS'ten
 * yönetebiliyoruz ve her çağrıda renk yazmak gerekmiyor.
 */
export function renderIcon(name: IconName, options: IconOptions): string {
  const { size, color = 'currentColor', strokeWidth = 1.8 } = options;

  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none"
    stroke="${color}" stroke-width="${strokeWidth}"
    stroke-linecap="round" stroke-linejoin="round"
    aria-hidden="true" focusable="false">${ICON_SHAPES[name]}</svg>`;
}

/**
 * Onay işareti (✓).
 *
 * `IconName` listesinde yok çünkü bir alışkanlık ikonu değil; yalnızca
 * "tamamlandı" durumunu gösteren arayüz işareti. Diğer ikonlardan daha
 * kalın çizilir ki küçük dairelerin içinde net görünsün.
 */
export function renderCheckIcon(size: number, color = 'white', strokeWidth = 3.2): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none"
    stroke="${color}" stroke-width="${strokeWidth}"
    stroke-linecap="round" stroke-linejoin="round"
    aria-hidden="true" focusable="false"><path d="M4 12l5 5 11-11"/></svg>`;
}

/**
 * Artı işareti (+).
 *
 * `renderCheckIcon` gibi, bu da `IconName` listesinde değil: bir
 * alışkanlık ikonu değil, "ekle" eylemini anlatan arayüz işareti.
 */
export function renderPlusIcon(size: number, color = 'white', strokeWidth = 2.6): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none"
    stroke="${color}" stroke-width="${strokeWidth}"
    stroke-linecap="round" stroke-linejoin="round"
    aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14"/></svg>`;
}
